import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Music, Search, Plus, X, Loader2, Disc3, ListMusic, Play, Pause, SkipForward, Radio, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Poll a room's Spotify jukebox status
export function useSpotify(code, intervalMs = 3000) {
  const [sp, setSp] = useState(null);
  useEffect(() => {
    if (!code) return;
    let alive = true;
    const poll = async () => {
      try { const r = await api.get(`/rooms/${code}/spotify`); if (alive) setSp(r.data); } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, intervalMs);
    return () => { alive = false; clearInterval(id); };
  }, [code, intervalMs]);
  return sp;
}

export function ConnectSpotifyButton({ code, hostToken, compact }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/spotify/login`, { params: { code, host_token: hostToken } });
      window.location.href = r.data.auth_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start Spotify"); setBusy(false); }
  };
  return (
    <button data-testid="spotify-connect-btn" onClick={connect} disabled={busy}
      className={`flex items-center justify-center gap-2 rounded-2xl font-head font-bold text-white bg-[#1DB954] hover:brightness-110 transition-[filter] ${compact ? "px-4 py-2.5 text-sm" : "w-full py-4"}`}>
      {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Music className="w-5 h-5" />} Connect Spotify
    </button>
  );
}

function JamHelper() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#1DB954]/10 rounded-2xl border border-[#1DB954]/30 mb-3 shrink-0 overflow-hidden">
      <button data-testid="jukebox-jam-toggle" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 p-3 text-left">
        <div className="w-9 h-9 rounded-full bg-[#1DB954]/20 flex items-center justify-center shrink-0"><Radio className="w-5 h-5 text-[#1DB954]" /></div>
        <div className="min-w-0 flex-1">
          <p className="font-head font-bold text-white text-sm">Want everyone adding songs?</p>
          <p className="font-head text-[#1DB954] text-xs">Start a Spotify Jam · tap for steps</p>
        </div>
        <ChevronDown className={`w-5 h-5 text-white/50 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="px-3.5 pb-3.5">
            <ol className="space-y-1.5 text-white/75 font-head text-sm list-decimal list-inside">
              <li>Open the <b className="text-white">Spotify app</b> on your phone and play any track.</li>
              <li>Tap the <b className="text-white">speaker / Connect</b> icon (bottom-left of the Now Playing bar).</li>
              <li>Tap <b className="text-white">Start a Jam</b>, then <b className="text-white">Invite</b>.</li>
              <li>Share the Jam link or QR with your players — they join in their own Spotify app.</li>
            </ol>
            <p className="text-white/40 text-xs font-head mt-2.5">Note: Spotify Jam needs the host to have Premium; guests use their own Spotify apps. This happens inside Spotify, separate from the in-game jukebox.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function JukeboxSheet({ code, sp, hostToken, isHost, playerName, mysteryMode, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);
  const tRef = useRef(null);

  const doSearch = useCallback(async (query) => {
    if (!query.trim()) { setResults([]); setLoading(false); return; }
    setLoading(true);
    try { const r = await api.get(`/rooms/${code}/spotify/search`, { params: { q: query } }); setResults(r.data.tracks || []); }
    catch (e) { toast.error("Search failed"); }
    setLoading(false);
  }, [code]);

  const onChange = (v) => { setQ(v); clearTimeout(tRef.current); tRef.current = setTimeout(() => doSearch(v), 400); };

  const add = async (t) => {
    setAdding(t.uri);
    try {
      await api.post(`/rooms/${code}/spotify/queue`, { host_token: hostToken, track_uri: t.uri, track_name: t.name, artist: t.artist, image: t.image, player_name: playerName });
      toast.success(`Added "${t.name}" 🎵`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add"); }
    setAdding(null);
  };

  const np = mysteryMode ? null : sp?.now_playing;
  const queue = sp?.queue || [];

  return createPortal(
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] bg-black/70 flex items-end sm:items-center justify-center" onClick={onClose}>
      <motion.div initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }} onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-[#140B22] border-t sm:border border-white/15 rounded-t-3xl sm:rounded-3xl p-5 max-h-[86vh] flex flex-col">
        <div className="flex items-center justify-between mb-3 shrink-0">
          <div className="flex items-center gap-2"><Disc3 className="w-6 h-6 text-[#1DB954]" /><h3 className="font-display font-black text-xl text-white">Party Jukebox</h3></div>
          <button data-testid="jukebox-close" onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center"><X className="w-5 h-5 text-white" /></button>
        </div>
        {isHost && <JamHelper />}
        {mysteryMode && (
          <div className="flex items-center gap-3 bg-[#1DB954]/10 rounded-2xl p-2.5 mb-3 border border-[#1DB954]/40 shrink-0">
            <div className="w-12 h-12 rounded-lg bg-[#1DB954]/20 flex items-center justify-center"><Disc3 className="w-6 h-6 text-[#1DB954]" /></div>
            <div className="min-w-0 flex-1"><p className="text-[#1DB954] text-xs font-head font-bold uppercase tracking-wide">Mystery track</p><p className="font-head font-bold text-white">🎵 Name that tune on the TV!</p></div>
          </div>
        )}
        {np && (
          <div className="flex items-center gap-3 bg-white/5 rounded-2xl p-2.5 mb-3 border border-white/10 shrink-0">
            {np.image ? <img src={np.image} alt="" className="w-12 h-12 rounded-lg" /> : <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center"><Music className="w-5 h-5 text-white/50" /></div>}
            <div className="min-w-0 flex-1"><p className="text-[#1DB954] text-xs font-head font-bold uppercase tracking-wide">Now playing</p><p className="font-head font-bold text-white truncate">{np.name}</p><p className="font-head text-white/50 text-sm truncate">{np.artist}</p></div>
          </div>
        )}
        {isHost && (
          <div className="relative mb-3 shrink-0">
            <Search className="w-5 h-5 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="jukebox-search-input" value={q} onChange={(e) => onChange(e.target.value)} placeholder="Search a song or artist…"
              className="w-full bg-white/5 border-2 border-white/15 rounded-2xl pl-10 pr-4 py-3 text-white font-head focus:border-[#1DB954] outline-none" />
          </div>
        )}
        <div className="flex-1 overflow-y-auto -mx-1 px-1">
          {isHost && loading && <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 text-[#1DB954] animate-spin" /></div>}
          {isHost && !loading && q && results.length === 0 && <p className="text-white/40 text-center font-head py-6 text-sm">No songs found</p>}
          {(!isHost || (!loading && !q)) && (
            <div>
              <p className="font-head uppercase tracking-widest text-white/40 text-xs mb-2 flex items-center gap-1.5"><ListMusic className="w-4 h-4" /> Up next ({queue.length})</p>
              {queue.length === 0 ? <p className="text-white/40 text-center font-head py-4 text-sm">{isHost ? "Search a song to kick off the party 🎉" : "The host hasn't queued anything yet 🎵"}</p>
                : queue.map((t, i) => (
                  <div key={i} className="flex items-center gap-3 py-2">
                    <span className="w-5 text-center font-display font-black text-white/30 text-sm shrink-0">{i + 1}</span>
                    {t.image ? <img src={t.image} alt="" className="w-10 h-10 rounded" /> : <div className="w-10 h-10 rounded bg-white/10" />}
                    <div className="min-w-0 flex-1"><p className="font-head font-semibold text-white text-sm truncate">{t.name}</p><p className="font-head text-white/45 text-xs truncate">{t.artist}</p></div>
                  </div>
                ))}
            </div>
          )}
          {isHost && !loading && results.map((t) => (
            <div key={t.uri} data-testid={`jukebox-track-${t.id}`} className="flex items-center gap-3 py-2">
              {t.image ? <img src={t.image} alt="" className="w-11 h-11 rounded" /> : <div className="w-11 h-11 rounded bg-white/10" />}
              <div className="min-w-0 flex-1"><p className="font-head font-semibold text-white text-sm truncate">{t.name}</p><p className="font-head text-white/45 text-xs truncate">{t.artist}</p></div>
              <button data-testid={`jukebox-add-${t.id}`} onClick={() => add(t)} disabled={adding === t.uri}
                className="shrink-0 w-10 h-10 rounded-full bg-[#1DB954] flex items-center justify-center disabled:opacity-60">{adding === t.uri ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Plus className="w-5 h-5 text-white" strokeWidth={3} />}</button>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>, document.body);
}

export function JukeboxBar({ code, hostToken, isHost, playerName, mysteryMode }) {
  const sp = useSpotify(code, isHost && hostToken ? 3000 : 6000);
  const [open, setOpen] = useState(false);
  if (!sp || !sp.configured) return null;

  if (!sp.connected) {
    if (!isHost || !hostToken) return null;
    return (
      <div className="px-4 pb-3 pt-2.5 border-t border-white/10 bg-[#0D0714] shrink-0">
        <ConnectSpotifyButton code={code} hostToken={hostToken} />
        <p className="text-center text-white/40 text-xs font-head mt-1.5">Add a Spotify Premium soundtrack to the party</p>
      </div>
    );
  }

  const np = mysteryMode ? null : sp.now_playing;
  const hostAct = async (action) => { try { await api.post(`/rooms/${code}/spotify/playback`, { host_token: hostToken, action }); } catch (e) { toast.error("Playback failed"); } };
  const canHost = isHost && hostToken;

  return (
    <>
      <div className="px-3 py-2.5 border-t border-white/10 bg-[#0D0714] flex items-center gap-3 shrink-0">
        <button data-testid="jukebox-open" onClick={() => setOpen(true)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-[#1DB954]/15 flex items-center justify-center overflow-hidden">{np?.image ? <img src={np.image} className="w-full h-full object-cover" alt="" /> : <Disc3 className="w-5 h-5 text-[#1DB954]" />}</div>
          <div className="min-w-0 flex-1">
            {mysteryMode ? <><p className="font-head font-bold text-white text-sm">🎵 Mystery track</p><p className="font-head text-[#1DB954] text-xs">Guess it on the TV!</p></>
              : np ? <><p className="font-head font-bold text-white text-sm truncate">{np.name}</p><p className="font-head text-white/45 text-xs truncate">{np.artist}</p></>
                : <><p className="font-head font-bold text-white text-sm">Party Jukebox</p><p className="font-head text-[#1DB954] text-xs">{canHost ? "Tap to add a song 🎵" : "Tap to see what's playing 🎵"}</p></>}
          </div>
        </button>
        {canHost ? (
          <div className="flex items-center gap-1.5 shrink-0">
            <button data-testid="jukebox-host-playpause" onClick={() => hostAct(np?.is_playing ? "pause" : "play")} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">{np?.is_playing ? <Pause className="w-4 h-4 text-white" fill="currentColor" /> : <Play className="w-4 h-4 text-white" fill="currentColor" />}</button>
            <button data-testid="jukebox-host-next" onClick={() => hostAct("next")} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center"><SkipForward className="w-4 h-4 text-white" fill="currentColor" /></button>
            <button data-testid="jukebox-add-btn" onClick={() => setOpen(true)} className="w-9 h-9 rounded-full bg-[#1DB954] flex items-center justify-center"><Plus className="w-5 h-5 text-white" strokeWidth={3} /></button>
          </div>
        ) : (
          <span className="shrink-0 rounded-full bg-white/10 px-3 py-1.5 font-head font-semibold text-white/60 text-xs flex items-center gap-1.5"><Disc3 className="w-3.5 h-3.5 text-[#1DB954]" /> Host's picks</span>
        )}
      </div>
      <AnimatePresence>{open && <JukeboxSheet code={code} sp={sp} hostToken={hostToken} isHost={canHost} playerName={playerName} mysteryMode={mysteryMode} onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
}
